import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Mail, ArrowLeft } from 'lucide-react';
import { authService } from '@/services/authService';
import { toast } from 'sonner';
import {
  trackFeatureUsage,
  trackFormSubmission,
  trackEvent,
} from '@/analytics/AnalyticsTracker';
import { useTranslation } from 'react-i18next';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToLogin: () => void;
}

const ForgotPasswordModal = ({
  isOpen,
  onClose,
  onBackToLogin,
}: ForgotPasswordModalProps) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { t: tAuth } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error(tCommon('validation.emailRequired'));
      return;
    }

    // Track form submission attempt
    trackFormSubmission('forgot_password_form', 'forgot_password_modal', {
      has_email: !!email,
    });

    setIsLoading(true);

    try {
      const response = await authService.forgotPassword({
        email: email.trim(),
      });

      // Track successful request
      trackEvent('forgot_password_success', {
        email_domain: email.split('@')[1] || 'unknown',
      });

      toast.success(
        response.message || tAuth('forgotPassword.toast.success.linkSent'),
      );

      // Reset form and close modal
      setEmail('');
      onClose();
    } catch (error) {
      console.error('Forgot password failed:', error);

      // Track error
      trackEvent('forgot_password_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : tAuth('forgotPassword.toast.error.linkFailed');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    // Track navigation back to login
    trackFeatureUsage('forgot_password_modal', 'back_to_login');

    setEmail('');
    onBackToLogin();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-md bg-gray-900 border-purple-500/20'>
        <DialogHeader>
          <div className='flex items-center justify-center relative'>
            <button
              type='button'
              onClick={handleBackToLogin}
              className='absolute start-0 text-gray-400 hover:text-gray-300'
            >
              <ArrowLeft className='rtl:rotate-180 w-5 h-5 rtl:rotate-180' />
            </button>
            <DialogTitle className='text-xl font-bold text-white'>
              {tAuth('forgotPassword.title')}
            </DialogTitle>
          </div>
          <DialogDescription className='text-center text-gray-400'>
            {tAuth('forgotPassword.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='forgot-email' className='text-gray-300'>
              {tAuth('forgotPassword.emailLabel')}
            </Label>
            <div className='relative'>
              <Mail className='absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <Input
                id='forgot-email'
                type='email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                className='ps-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500'
                placeholder={tAuth('forgotPassword.emailPlaceholder')}
                required
                autoComplete='email'
              />
            </div>
          </div>

          <Button
            type='submit'
            className='w-full bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90'
            disabled={isLoading}
          >
            {isLoading
              ? tAuth('forgotPassword.submitting')
              : tAuth('forgotPassword.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
